import { RefreshTracker } from "@compass/shared/utils/refreshTracker";

type TokenListener = () => void;

// PRTL-1356: renamed from "token" so every pre-hardening client is forced to
// re-login (old tokens have no server-side session and would be rejected).
export const TOKEN_STORAGE_KEY = "compass.authToken";
/**
 * PRTL-1348 masquerade tokens live in sessionStorage ONLY: per-tab, gone on
 * tab close, invisible to other tabs' storage listeners — so a masquerade
 * never clobbers the admin's own session in other tabs.
 */
export const MASQUERADE_STORAGE_KEY = "compass.masqueradeToken";
/**
 * PRTL-1519: when (by this device's clock) the saved token was received, so the refresh
 * rule can age it on one clock. `{ "token": <last 16 chars>, "at": <ms> }`; read
 * defensively, and ignored unless it names the current token.
 */
export const TOKEN_RECEIVED_AT_KEY = "compass.authTokenReceivedAt";

const tokenTag = (token: string) => token.slice(-16);

function readReceivedAt(token: string): number | null {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(TOKEN_RECEIVED_AT_KEY) ?? "null");
        if (!parsed || typeof parsed !== "object") return null;
        const { token: tag, at } = parsed as { token?: unknown; at?: unknown };
        return tag === tokenTag(token) && typeof at === "number" && Number.isFinite(at) ? at : null;
    } catch {
        return null;
    }
}

function writeReceivedAt(token: string | null): void {
    try {
        if (token === null) localStorage.removeItem(TOKEN_RECEIVED_AT_KEY);
        else {
            localStorage.setItem(
                TOKEN_RECEIVED_AT_KEY,
                JSON.stringify({ token: tokenTag(token), at: Date.now() }),
            );
        }
    } catch {
        // Storage full or blocked: the refresh rule falls back to the token's iat.
    }
}

class TokenManager {
    private token: string | null = null;
    /** The refresh in flight, if any (PRTL-1519): a 401 waits for it before giving up. */
    private refreshes = new RefreshTracker();
    private masqueradeToken: string | null = null;
    private listeners = new Set<TokenListener>();

    constructor() {
        this.token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? null;
        this.masqueradeToken = sessionStorage.getItem(MASQUERADE_STORAGE_KEY) ?? null;
    }

    public get = () => {
        if (this.masqueradeToken) return this.masqueradeToken;
        return this.token === "" ? null : this.token;
    };

    public isMasquerading = () => this.masqueradeToken !== null;

    /** Marks `refresh` as the refresh in flight until it settles. */
    public trackRefresh = <T>(refresh: Promise<T>): Promise<T> => this.refreshes.track(refresh);

    /** Resolves once no refresh is in flight (whatever its outcome). */
    public waitForRefresh = (): Promise<void> => this.refreshes.wait();

    /** When this device received the saved (non-masquerade) token, or null if unknown. */
    public receivedAt = (token: string): number | null => readReceivedAt(token);

    public setMasquerade = (token: string) => {
        this.masqueradeToken = token;
        sessionStorage.setItem(MASQUERADE_STORAGE_KEY, token);
        this.listeners.forEach((listener) => listener());
    };

    /** Exit masquerade; the admin's own token (if any) takes over again. */
    public clearMasquerade = () => {
        this.masqueradeToken = null;
        sessionStorage.removeItem(MASQUERADE_STORAGE_KEY);
        this.listeners.forEach((listener) => listener());
    };

    public set = (newToken: string | null) => {
        // A logout/401 while masquerading exits the masquerade only — the
        // admin's own token in localStorage must survive.
        if (newToken === null && this.masqueradeToken) {
            this.clearMasquerade();
            return;
        }
        this.token = newToken;
        writeReceivedAt(newToken);
        if (newToken === null) {
            // removeItem (vs writing "") so other tabs' storage listeners see
            // newValue === null and treat it as a logout.
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        } else {
            localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        }
        this.listeners.forEach((listener) => listener());
    };

    public subscribe = (listener: TokenListener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
}

export const tokenManager = new TokenManager();
